import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckSquare, Check } from 'lucide-react';
import { getModuleWorkstreams } from '../../config/moduleHelpers';

const BulkStatusModal = ({ selectedClients, config, onUpdateStatus, onClose }) => {
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedWorkstream, setSelectedWorkstream] = useState('');
  const workstreams = getModuleWorkstreams(config);

  const handleUpdate = () => {
    if (selectedStatus && selectedWorkstream) {
      onUpdateStatus(selectedWorkstream, selectedStatus);
      onClose();
    }
  };

  const getStatusOptions = (workstream) => {
    // This would ideally come from config, but for now, common statuses
    return [
      { value: 'not_started', label: 'Not Started' },
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'cancelled', label: 'Cancelled' }
    ];
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-6 h-6 text-green-600" />
            <h3 className="text-lg font-semibold text-slate-800">Bulk Update Status</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Update status for {selectedClients.length} selected client{selectedClients.length !== 1 ? 's' : ''}.
          </p>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Workstream</label>
            <select
              value={selectedWorkstream}
              onChange={(e) => {
                setSelectedWorkstream(e.target.value);
                setSelectedStatus(''); // Reset status when workstream changes
              }}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-800 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              style={{ colorScheme: 'light' }}
            >
              <option value="" className="bg-white text-slate-800">Select workstream...</option>
              {workstreams.filter(ws => ws.statusField).map(ws => (
                <option key={ws.key} value={ws.key} className="bg-white text-slate-800">{ws.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              disabled={!selectedWorkstream}
              className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-800 focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:opacity-50"
              style={{ colorScheme: 'light' }}
            >
              <option value="" className="bg-white text-slate-800">Select status...</option>
              {selectedWorkstream && getStatusOptions(workstreams.find(ws => ws.key === selectedWorkstream)).map(status => (
                <option key={status.value} value={status.value} className="bg-white text-slate-800">{status.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={!selectedStatus || !selectedWorkstream}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            Update
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BulkStatusModal;